// Kanban board grouped by status / type / assignee / priority.
// Drag a job card between columns to mutate the grouping field on
// the server; the shell supplies sorted jobs so each column inherits
// the sort order.
import { useMemo, useState } from "react";
import type {
  GroupBy,
  JobDto,
  JobPriority,
  JobStatusDto,
  SolutionTypeDto,
  TeamMemberDto,
} from "../../../../../../shared/types/production-schedule";
import { JOB_PRIORITIES } from "../../../../../../shared/types/production-schedule";
import { JobCard } from "../components/JobCard";
import { useActiveJobStatuses } from "../components/JobStatusesProvider";
import { updateJob } from "../hooks/useProductionApi";

interface Props {
  jobs: JobDto[];
  team: TeamMemberDto[];
  types: SolutionTypeDto[];
  groupBy: GroupBy;
  onOpenJob(id: number): void;
  onJobUpdated?(): void | Promise<void>;
}

interface Column {
  key: string;
  label: string;
  color: string;
  jobs: JobDto[];
}

const PRIORITY_COLOR: Record<JobPriority, string> = {
  Urgent: "#B3261E",
  High: "#C77700",
  Normal: "#1B449C",
  Low: "#2E7D32",
};

const UNASSIGNED_KEY = "_";

// Columns come from the live catalogue (non-archived, sortOrder).
// A job whose status doesn't match any catalogue row (e.g. the label
// was archived but the job stayed on it) still needs a column so
// the user can see + move it — synthesise an "Other" bucket only if
// at least one such orphan exists.
function columnsForStatus(jobs: JobDto[], catalogue: JobStatusDto[]): Column[] {
  const cols: Column[] = catalogue.map((s) => ({
    key: s.label,
    label: s.label,
    color: s.color,
    jobs: jobs.filter((j) => j.status === s.label),
  }));
  const known = new Set(catalogue.map((s) => s.label));
  const orphans = jobs.filter((j) => !known.has(j.status));
  if (orphans.length > 0) {
    cols.push({
      key: "_orphan",
      label: "Other",
      color: "#9CA09F",
      jobs: orphans,
    });
  }
  return cols;
}

function columnsForType(jobs: JobDto[], types: SolutionTypeDto[]): Column[] {
  const cols = types.map<Column>((t) => ({
    key: String(t.id),
    label: t.name,
    color: "#1B449C",
    jobs: jobs.filter((j) => j.solutionType?.id === t.id),
  }));
  cols.push({
    key: UNASSIGNED_KEY,
    label: "No Type",
    color: "#9CA09F",
    jobs: jobs.filter((j) => j.solutionType === null),
  });
  return cols;
}

function columnsForAssignee(jobs: JobDto[], team: TeamMemberDto[]): Column[] {
  const cols = team.map<Column>((m) => ({
    key: String(m.id),
    label: m.name,
    color: m.color,
    jobs: jobs.filter((j) => j.assignee?.id === m.id),
  }));
  cols.push({
    key: UNASSIGNED_KEY,
    label: "Unassigned",
    color: "#9CA09F",
    jobs: jobs.filter((j) => j.assignee === null),
  });
  return cols;
}

function columnsForPriority(jobs: JobDto[]): Column[] {
  return JOB_PRIORITIES.map<Column>((p) => ({
    key: p,
    label: p,
    color: PRIORITY_COLOR[p],
    jobs: jobs.filter((j) => j.priority === p),
  }));
}

// Resolve a column key under the current grouping back into the
// patch payload that moves a job into that column. statusLabels
// comes from the live catalogue so a custom status accepts drops
// (the column shows the catalogue label as its key).
function patchForColumn(
  groupBy: GroupBy,
  colKey: string,
  statusLabels: readonly string[],
): Record<string, unknown> | null {
  if (groupBy === "status") {
    if (colKey === "_orphan") return null;
    if (!statusLabels.includes(colKey)) return null;
    return { status: colKey };
  }
  if (groupBy === "priority") {
    if (!(JOB_PRIORITIES as readonly string[]).includes(colKey)) return null;
    return { priority: colKey };
  }
  if (groupBy === "assignee") {
    if (colKey === UNASSIGNED_KEY) return { assigneeId: null };
    const id = Number(colKey);
    return Number.isFinite(id) ? { assigneeId: id } : null;
  }
  if (groupBy === "type") {
    if (colKey === UNASSIGNED_KEY) return { solutionTypeId: null };
    const id = Number(colKey);
    return Number.isFinite(id) ? { solutionTypeId: id } : null;
  }
  return null;
}

export function BoardView({
  jobs,
  team,
  types,
  groupBy,
  onOpenJob,
  onJobUpdated,
}: Props) {
  const activeStatuses = useActiveJobStatuses();
  const statusLabels = useMemo(() => activeStatuses.map((s) => s.label), [activeStatuses]);

  let columns: Column[];
  if (groupBy === "type") columns = columnsForType(jobs, types);
  else if (groupBy === "assignee") columns = columnsForAssignee(jobs, team);
  else if (groupBy === "priority") columns = columnsForPriority(jobs);
  else columns = columnsForStatus(jobs, activeStatuses);

  const [dragJobId, setDragJobId] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onDrop = async (colKey: string) => {
    setDropTarget(null);
    if (dragJobId === null || busy) return;
    const job = jobs.find((j) => j.id === dragJobId);
    setDragJobId(null);
    if (!job) return;
    const patch = patchForColumn(groupBy, colKey, statusLabels);
    if (!patch) return;
    // Skip the round-trip if the column hasn't actually changed.
    const noop = Object.entries(patch).every(([k, v]) => {
      if (k === "assigneeId") return (job.assignee?.id ?? null) === v;
      if (k === "solutionTypeId") return (job.solutionType?.id ?? null) === v;
      return (job as unknown as Record<string, unknown>)[k] === v;
    });
    if (noop) return;
    setBusy(true);
    try {
      await updateJob(job.id, patch);
      await onJobUpdated?.();
    } catch (e) {
      window.alert(`Failed to move job: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="kanban-wrap">
      <div className="kanban-board">
        {columns.map((col) => {
          const isTarget = dropTarget === col.key && dragJobId !== null;
          return (
            <div
              key={col.key}
              className={`kanban-col${isTarget ? " is-drop-target" : ""}`}
              onDragOver={(e) => {
                if (dragJobId === null) return;
                e.preventDefault();
                if (dropTarget !== col.key) setDropTarget(col.key);
              }}
              onDragLeave={(e) => {
                if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                if (dropTarget === col.key) setDropTarget(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                void onDrop(col.key);
              }}
            >
              <div className="col-header">
                <div className="col-dot" style={{ background: col.color }} />
                <div className="col-title">{col.label}</div>
                <div className="col-count">{col.jobs.length}</div>
              </div>
              {col.jobs.length === 0 ? (
                <div className="col-empty">No jobs</div>
              ) : (
                col.jobs.map((j) => (
                  <DraggableJobCard
                    key={j.id}
                    job={j}
                    types={types}
                    dragging={dragJobId === j.id}
                    onDragStart={() => setDragJobId(j.id)}
                    onDragEnd={() => { setDragJobId(null); setDropTarget(null); }}
                    onOpen={onOpenJob}
                  />
                ))
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DraggableJobCard({
  job,
  types,
  dragging,
  onDragStart,
  onDragEnd,
  onOpen,
}: {
  job: JobDto;
  types: SolutionTypeDto[];
  dragging: boolean;
  onDragStart(): void;
  onDragEnd(): void;
  onOpen(id: number): void;
}) {
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", `job:${job.id}`);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      style={{ opacity: dragging ? 0.45 : 1 }}
    >
      <JobCard job={job} types={types} onOpen={onOpen} />
    </div>
  );
}
